import {
  CanvasTexture,
  SRGBColorSpace,
  MeshStandardMaterial,
  ClampToEdgeWrapping,
  LinearFilter,
} from "three";

const COIN_FACE_RADIUS = 0.405;
const TEXTURE_SIZE = 512;
const FONT_NAME = '"Galano"';
const NORMAL_TEXTURE_DPR_CAP = 2;
const ZOOM_TEXTURE_DPR_CAP = 2.5;
const EXTREME_ZOOM_TEXTURE_DPR_CAP = 3;
const ZOOM_TEXTURE_DPR_THRESHOLD = 2;
const EXTREME_ZOOM_TEXTURE_DPR_THRESHOLD = 3.5;

const COIN_COLORWAYS = {
  'blue': {
    dark: '#003BB2',
    light: '#5A8CF2',
    borderText: 'COINS',
    borderTextSingular: 'COIN',
    borderTextTop: 'JOY',
  },
  'orange': {
    dark: '#E8800F',
    light: '#FFC196',
    borderText: 'COINS',
    borderTextSingular: 'COIN',
    borderTextTop: 'JOY',
  }
};

// SVG path data from Group.svg
// ViewBox: 0 0 150 201
const SVG_PATHS = [
  "M147.813 69.9922C150.938 88.6797 149.219 107.836 142.844 125.68C122.188 183.805 58.3438 214.211 0.218749 193.555L0 193.461L13.0937 156.586L13.3125 156.68C25.6875 161.086 39.0625 162.023 51.9375 159.43C64.8125 156.836 76.7812 150.773 86.4687 141.93C96.1875 133.086 103.313 121.742 107.063 109.148C110.844 96.5547 111.125 83.1797 107.875 70.4297L147.75 70.0234L147.813 69.9922Z",
  "M108.197 2.34676C112.541 0.284259 117.384 -0.434497 122.134 0.253003C126.884 0.940503 131.322 3.03426 134.884 6.25301C138.447 9.47176 140.978 13.6592 142.166 18.3155C143.353 22.9717 143.103 27.878 141.509 32.378C139.353 38.4405 134.884 43.4092 129.072 46.1592C123.259 48.9092 116.572 49.253 110.509 47.128C105.978 45.5343 102.041 42.5968 99.1655 38.753C96.2905 34.9093 94.6343 30.2843 94.3843 25.503C94.1343 20.7218 95.3218 15.9405 97.7905 11.8155C100.259 7.69051 103.884 4.378 108.228 2.3155L108.197 2.34676Z"
];
const SVG_VIEWBOX_WIDTH = 150;
const SVG_VIEWBOX_HEIGHT = 201;

// Reference dimensions for proportional scaling
// Reference: coin face width 420px, logo width 149.331px
// Reference: logo offset 117px from left, 102px from top (assuming reference texture size matches TEXTURE_SIZE)
const REFERENCE_COIN_FACE_WIDTH = 420;
const REFERENCE_LOGO_WIDTH = 149.331;
const REFERENCE_LOGO_OFFSET_X = 117;
const REFERENCE_LOGO_OFFSET_Y = 102;
const REFERENCE_LABEL_SIZE = 175.5;
const REFERENCE_BORDER_TEXT_SIZE = 32;
const REFERENCE_BORDER_TEXT_RADIUS = 125;

// Base angle per character for border text (in radians, negative for counter-clockwise)
const BASE_ANGLE_PER_CHAR = Math.PI * -0.05;

// Character width adjustments (in radians) to add to base anglePerChar
// Negative values reduce spacing (narrow chars), positive increase spacing (wide chars)
// Only characters actually used in borderText are mapped
const CHAR_WIDTH_ADJUSTMENTS = {
  'J': -0.06,
  'O': -0.03,
  'Y': -0.04,
  'C': -0.12,
  'S': -0.04,
  'N': -0.06,
  'I': 0.01,
  '(': 0.04,
  ')': 0.04,
};

const CHAR_WIDTH_FALLBACK = 0; // Default adjustment for unmapped characters

// Font loading timeout (fallback if Font Loading API unavailable)
const FONT_LOAD_TIMEOUT = 2000; // 2 seconds

class CoinTextureManager {
  constructor({ devicePixelRatio = window.devicePixelRatio || 1, textureAnisotropy = 1 } = {}) {
    // Cache for top face textures (keyed by label)
    this.topFaceTextureCache = new Map();

    // Cache for bottom face textures (keyed by colorway)
    this.bottomFaceTextureCache = new Map();

    // Track materials so texture objects can be replaced cleanly on zoom-tier changes.
    this.topMaterialRegistry = new Map();
    this.bottomMaterialRegistry = new Map();

    // Cache for side materials (keyed by colorway)
    this.sideMaterialCache = new Map();

    // Cache for font loading promise
    this.fontReadyPromise = null;

    this.texturePixelRatio = this.getTargetTexturePixelRatio(devicePixelRatio);
    this.textureAnisotropy = Math.max(1, Math.floor(textureAnisotropy));
  }

  /**
   * Wait for the Galano font to be loaded before rendering text
   * Uses Font Loading API if available, otherwise falls back to timeout
   * @returns {Promise<void>}
   */
  waitForFontReady() {
    // Return cached promise if already created
    if (this.fontReadyPromise) {
      return this.fontReadyPromise;
    }

    // Check if Font Loading API is available
    if (typeof document !== 'undefined' && document.fonts && document.fonts.check) {
      // Use Font Loading API
      const fontString = `700 32px ${FONT_NAME}`;

      // Check if font is already loaded
      if (document.fonts.check(fontString)) {
        this.fontReadyPromise = Promise.resolve();
        return this.fontReadyPromise;
      }

      // Wait for font to load
      this.fontReadyPromise = document.fonts.ready.then(() => {
        // Double-check the specific font is loaded
        if (document.fonts.check(fontString)) {
          return;
        }
        // If still not loaded, try loading it explicitly
        return document.fonts.load(fontString).catch(() => {
          // If load fails, proceed anyway (font may be available but API doesn't detect it)
          console.warn('Font loading check failed, proceeding with texture generation');
        });
      }).catch(() => {
        // If Font Loading API fails, proceed after timeout
        return new Promise((resolve) => {
          setTimeout(resolve, FONT_LOAD_TIMEOUT);
        });
      });
    } else {
      // Fallback: wait for a reasonable timeout to allow fonts to load
      this.fontReadyPromise = new Promise((resolve) => {
        // If document.fonts is not available, wait a bit for fonts to load naturally
        setTimeout(resolve, FONT_LOAD_TIMEOUT);
      });
    }

    return this.fontReadyPromise;
  }

  getTargetTexturePixelRatio(devicePixelRatio = window.devicePixelRatio || 1) {
    if (devicePixelRatio > EXTREME_ZOOM_TEXTURE_DPR_THRESHOLD) {
      return EXTREME_ZOOM_TEXTURE_DPR_CAP;
    }

    if (devicePixelRatio > ZOOM_TEXTURE_DPR_THRESHOLD) {
      return ZOOM_TEXTURE_DPR_CAP;
    }

    return Math.min(devicePixelRatio, NORMAL_TEXTURE_DPR_CAP);
  }

  // Helper: Get the size of the texture from a reference size
  getSizeFromReference(referenceSize) {
    return Math.round(this.getCanvasSize() * (referenceSize / REFERENCE_COIN_FACE_WIDTH));
  }

  // Helper: Get canvas size accounting for device pixel ratio
  getCanvasSize() {
    return Math.round(TEXTURE_SIZE * this.texturePixelRatio);
  }

  // Helper: Create a canvas with the correct size
  createCanvas() {
    const size = this.getCanvasSize();
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    return { canvas, size, ctx: canvas.getContext('2d') };
  }

  // Helper: Create a THREE.Texture from a canvas
  createTextureFromCanvas(canvas) {
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.wrapS = ClampToEdgeWrapping;
    texture.wrapT = ClampToEdgeWrapping;
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.generateMipmaps = false;
    texture.anisotropy = this.textureAnisotropy;
    texture.needsUpdate = true;
    return texture;
  }

  clearTextureEntryCanvas(ctx, size) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, size, size);
  }

  registerMaterial(registry, key, material) {
    if (!registry.has(key)) {
      registry.set(key, new Set());
    }

    registry.get(key).add(material);
  }

  updateRegisteredMaterials(registry, key, texture) {
    const materials = registry.get(key);
    if (!materials) {
      return;
    }

    materials.forEach((material) => {
      material.map = texture;
      material.needsUpdate = true;
    });
  }

  buildTopFaceTextureEntry(label, colorway) {
    const { canvas, size, ctx } = this.createCanvas();
    this.clearTextureEntryCanvas(ctx, size);
    this.drawTopFaceTexture(ctx, size, COIN_COLORWAYS[colorway], label);

    return {
      texture: this.createTextureFromCanvas(canvas),
      canvas,
      ctx,
      label,
      colorway,
    };
  }

  buildBottomFaceTextureEntry(colorway) {
    const { canvas, size, ctx } = this.createCanvas();
    this.clearTextureEntryCanvas(ctx, size);
    this.drawBottomFaceTexture(ctx, size, COIN_COLORWAYS[colorway]);

    return {
      texture: this.createTextureFromCanvas(canvas),
      canvas,
      ctx,
      colorway,
    };
  }

  async updateTextureQuality({
    devicePixelRatio = window.devicePixelRatio || 1,
    textureAnisotropy = this.textureAnisotropy,
  } = {}) {
    const nextTexturePixelRatio = this.getTargetTexturePixelRatio(devicePixelRatio);
    const nextTextureAnisotropy = Math.max(1, Math.floor(textureAnisotropy));

    if (
      nextTexturePixelRatio === this.texturePixelRatio &&
      nextTextureAnisotropy === this.textureAnisotropy
    ) {
      return false;
    }

    this.texturePixelRatio = nextTexturePixelRatio;
    this.textureAnisotropy = nextTextureAnisotropy;

    if (this.topFaceTextureCache.size > 0) {
      await this.waitForFontReady();
    }

    this.topFaceTextureCache.forEach((entry, cacheKey) => {
      const nextEntry = this.buildTopFaceTextureEntry(entry.label, entry.colorway);
      this.topFaceTextureCache.set(cacheKey, nextEntry);
      this.updateRegisteredMaterials(this.topMaterialRegistry, cacheKey, nextEntry.texture);
      entry.texture.dispose();
    });

    this.bottomFaceTextureCache.forEach((entry, cacheKey) => {
      const nextEntry = this.buildBottomFaceTextureEntry(entry.colorway);
      this.bottomFaceTextureCache.set(cacheKey, nextEntry);
      this.updateRegisteredMaterials(this.bottomMaterialRegistry, cacheKey, nextEntry.texture);
      entry.texture.dispose();
    });

    return true;
  }

  // Helper: Draw the background gradient for coin faces
  drawFaceBackground(ctx, size, colorway) {
    const cx = size / 2;
    const cy = size / 2;
    const radius = size * COIN_FACE_RADIUS;

    const face = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    face.addColorStop(0, colorway.light);
    face.addColorStop(0.99, colorway.light);
    face.addColorStop(1, colorway.dark);
    ctx.fillStyle = face;
    ctx.fillRect(0, 0, size, size);

  }

  async getCoinTopFaceTexture(label, colorway) {
    const cacheKey = `${label}-${colorway}`;

    if (this.topFaceTextureCache.has(cacheKey)) {
      return this.topFaceTextureCache.get(cacheKey).texture;
    }

    // Wait for font to be ready before rendering text
    await this.waitForFontReady();

    const entry = this.buildTopFaceTextureEntry(label, colorway);
    this.topFaceTextureCache.set(cacheKey, entry);
    return entry.texture;
  }

  getCoinBottomFaceTexture(colorway) {
    if (this.bottomFaceTextureCache.has(colorway)) {
      return this.bottomFaceTextureCache.get(colorway).texture;
    }

    const entry = this.buildBottomFaceTextureEntry(colorway);
    this.bottomFaceTextureCache.set(colorway, entry);
    return entry.texture;
  }

  getCoinSideMaterial(colorway) {
    if (this.sideMaterialCache.has(colorway)) {
      return this.sideMaterialCache.get(colorway);
    }

    const material = new MeshStandardMaterial({
      color: COIN_COLORWAYS[colorway].dark,
    });

    this.sideMaterialCache.set(colorway, material);
    return material;
  }

  async getCoinMaterials(label, colorway = 'blue') {
    const sideMaterial = this.getCoinSideMaterial(colorway);
    const topCacheKey = `${label}-${colorway}`;
    const topTexture = await this.getCoinTopFaceTexture(label, colorway);
    const bottomTexture = this.getCoinBottomFaceTexture(colorway);

    const topMaterial = new MeshStandardMaterial({ map: topTexture });
    const bottomMaterial = new MeshStandardMaterial({ map: bottomTexture });

    this.registerMaterial(this.topMaterialRegistry, topCacheKey, topMaterial);
    this.registerMaterial(this.bottomMaterialRegistry, colorway, bottomMaterial);

    return [sideMaterial, topMaterial, bottomMaterial];
  }

  // Helper: Draw border text at a given radius offset (positive = bottom, negative = top)
  // reverse: if true, reverses the character order for top arc
  drawBorderText(ctx, cx, cy, borderTextChars, charPositions, totalArc, borderTextRadius, radiusOffset, reverse = false) {
    const charSize = this.getSizeFromReference(REFERENCE_BORDER_TEXT_SIZE);
    ctx.font = `700 ${charSize}px ${FONT_NAME}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (reverse) {
      // For top arc: reverse character order and mirror positions
      for (let i = borderTextChars.length - 1; i >= 0; i--) {
        const char = borderTextChars[i];
        const originalPosition = charPositions[i] - totalArc * 0.5;
        // Mirror the position to the opposite side
        const mirroredPosition = -originalPosition;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(mirroredPosition);
        ctx.translate(0, radiusOffset);
        ctx.fillText(char, 0, 0);
        ctx.restore();
      }
    } else {
      // For bottom arc: normal order
      borderTextChars.forEach((char, index) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(charPositions[index] - totalArc * 0.5);
        ctx.translate(0, radiusOffset);
        ctx.fillText(char, 0, 0);
        ctx.restore();
      });
    }
  }

  drawTopFaceTexture(ctx, size, colorway, label) {
    this.drawFaceBackground(ctx, size, colorway);

    // Label text
    const cx = size / 2;
    const cy = size / 2;
    ctx.fillStyle = colorway.dark;
    const labelSize = this.getSizeFromReference(REFERENCE_LABEL_SIZE);
    ctx.font = `700 ${labelSize}px ${FONT_NAME}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, cx, cy);

    // Draw border text
    ctx.fillStyle = colorway.dark;
    const borderTextRadius = this.getSizeFromReference(REFERENCE_BORDER_TEXT_RADIUS);

    // Bottom border text (COIN/COINS)
    const borderTextBottom = label === '1' ? colorway.borderTextSingular : colorway.borderText;
    const borderTextBottomChars = borderTextBottom.split('');

    // Calculate angle for each character and their positions for bottom text
    const charAnglesBottom = borderTextBottomChars.map((char) => {
      const charAdjustment = CHAR_WIDTH_ADJUSTMENTS[char] ?? CHAR_WIDTH_FALLBACK;
      return BASE_ANGLE_PER_CHAR + charAdjustment;
    });

    // Calculate total arc length for bottom text
    const totalArcBottom = charAnglesBottom.reduce((sum, angle) => sum + angle, 0);

    // Calculate position for each character (centered in its allocated space) for bottom text
    let cumulativeAngle = 0;
    const charPositionsBottom = charAnglesBottom.map((charAngle) => {
      const position = cumulativeAngle + charAngle * 0.5; // Center of allocated space
      cumulativeAngle += charAngle;
      return position;
    });

    // Draw border text at bottom
    this.drawBorderText(ctx, cx, cy, borderTextBottomChars, charPositionsBottom, totalArcBottom, borderTextRadius, borderTextRadius, false);

    // Top border text (JOY)
    const borderTextTop = colorway.borderTextTop;
    const borderTextTopChars = borderTextTop.split('');

    // Calculate angle for each character and their positions for top text
    const charAnglesTop = borderTextTopChars.map((char) => {
      const charAdjustment = CHAR_WIDTH_ADJUSTMENTS[char] ?? CHAR_WIDTH_FALLBACK;
      return BASE_ANGLE_PER_CHAR + charAdjustment;
    });

    // Calculate total arc length for top text
    const totalArcTop = charAnglesTop.reduce((sum, angle) => sum + angle, 0);

    // Calculate position for each character (centered in its allocated space) for top text
    cumulativeAngle = 0;
    const charPositionsTop = charAnglesTop.map((charAngle) => {
      const position = cumulativeAngle + charAngle * 0.5; // Center of allocated space
      cumulativeAngle += charAngle;
      return position;
    });

    // Draw border text at top (reversed character order)
    this.drawBorderText(ctx, cx, cy, borderTextTopChars, charPositionsTop, totalArcTop, borderTextRadius, -borderTextRadius, true);
  }

  drawBottomFaceTexture(ctx, size, colorway) {
    // Flip the entire texture 180 degrees
    ctx.save();
    ctx.translate(size, size);
    ctx.rotate(Math.PI);

    this.drawFaceBackground(ctx, size, colorway);

    // Scale SVG proportionally based on reference dimensions
    // Calculate logo width based on reference ratio, scaled to our texture size
    const logoWidth = this.getSizeFromReference(REFERENCE_LOGO_WIDTH);
    const svgScale = logoWidth / SVG_VIEWBOX_WIDTH;

    // Position SVG with scaled offsets from reference
    const offsetX = this.getSizeFromReference(REFERENCE_LOGO_OFFSET_X);
    const offsetY = this.getSizeFromReference(REFERENCE_LOGO_OFFSET_Y);
    const svgX = offsetX;
    const svgY = offsetY;

    ctx.save();
    ctx.translate(svgX, svgY);
    ctx.scale(svgScale, svgScale);

    // Fill with colorway dark color
    ctx.fillStyle = colorway.dark;

    // Draw each path
    SVG_PATHS.forEach((pathData) => {
      const path = new Path2D(pathData);
      ctx.fill(path);
    });

    ctx.restore();
    ctx.restore();
  }
}

export default CoinTextureManager;
